import React, {useContext, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {
  Legend,
  LegendBoxGreen,
  LegendBoxRed,
  OffersWrapper
} from 'components/AvailabilityDatePicker/AvailabilityHelpers'
import {TaxationAndPriceMessage} from 'components/TaxationAndPriceMessage'
import {
  getHotel,
  getHotelAvailabilityPricesComplete,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Icon} from '@vio/atlas/Icon'
import {OverlayPage, OverlayPageContent} from '@vio/atlas/OverlayPage'
import {Text} from '@vio/atlas/Text'
import {AvailabilityHotelEntity} from '@vio/core/src/availability/types'
import {ConfigureI18nContext} from '@vio/core/src/localization/components/ConfigureI18n'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {getCheckOutDate} from '@vio/core/src/searchParams/validateSearchParamDates'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'
import {AvailabilityDatePicker} from '@vio/shared/src/search/DatePicker/AvailabilityDatePicker'
import {WeekDays} from '@vio/shared/src/search/DatePickerOverlay/WeekDays'

import {OffersList} from '../../AvailabilityDatePicker/OffersList'

interface Props {
  /** Hotel ID */
  hotelId: string
  /** Selected date */
  selectedDate: string | null | undefined
  /** Date of Check In */
  checkIn: string
  /** Whether the overlay is open */
  isOpen: boolean
  /** The day click handler */
  onDayClick: (date: string) => void
  /** The callback for closing the overlay  */
  onClose: () => void
  /** Earliest Date that the user can select on the date picker for checkin */
  earliestCheckInDate?: Date
  /** Offers data to be displayed in the overlay */
  offersData?: SearchOffer[]
  /** Number of nights stay */
  nights: number
  /** Hotel availability data */
  hotelAvailabilityPrices: AvailabilityHotelEntity
  /** The callback for when the month changes */
  onMonthChange: (date: Date) => void
  /** The source component name for tracking */
  sourceComponent: string
}

export const DatePickerOverlay = ({
  hotelId,
  selectedDate,
  checkIn,
  isOpen,
  onDayClick,
  onClose,
  earliestCheckInDate,
  offersData,
  nights,
  hotelAvailabilityPrices,
  onMonthChange,
  sourceComponent
}: Props) => {
  const {formatDate} = useIntl()
  const containerRef = useRef<null | HTMLDivElement>(null)
  const searchComplete = useSelector(getHotelAvailabilityPricesComplete)
  const {monthNames, weekDayNamesShort, firstDayOfWeek} =
    useContext(ConfigureI18nContext)
  const hotelDetails = useSelector(state => getHotel(state, hotelId))
  const showTotalPrices = useSelector(getShowTotalPrices)

  return (
    <OverlayPage
      isOpen={isOpen}
      navBarProps={{
        centerContent: hotelDetails?.hotelName
      }}
      onClose={onClose}
      dataId="AvailabilityPopup"
    >
      <WeekDays
        firstDayOfWeek={firstDayOfWeek}
        weekdaysShort={weekDayNamesShort}
        shouldHighlightWeekend
      />
      <OverlayPageContent ref={containerRef}>
        <>
          <AvailabilityDatePicker
            numberOfMonthsToShow={1}
            firstDayOfWeek={firstDayOfWeek}
            weekdaysShort={weekDayNamesShort}
            selectedDate={selectedDate}
            months={monthNames}
            variant="horizontal"
            earliestCheckInDate={earliestCheckInDate}
            onDayClick={onDayClick}
            hotelAvailabilityPrices={hotelAvailabilityPrices}
            checkIn={checkIn}
            onMonthChange={onMonthChange}
            searchComplete={searchComplete}
            showTotalPrices={showTotalPrices}
          />

          <Box padding="s500" paddingTop="s000">
            <Text
              variant="bodyXS"
              colorPath="content.neutral.secondary"
              align="right"
            >
              <TaxationAndPriceMessage showCurrencyCode hotelId={hotelId} />
            </Text>

            <Legend>
              <Grid container spacing="s200" justify="flex-end">
                <Grid mobileSm="fit">
                  <LegendBoxGreen />
                </Grid>
                <Grid mobileSm="fit">
                  <Text variant="bodyXS" colorPath="content.neutral.secondary">
                    <FormattedMessageWrapper
                      id="cheaper"
                      defaultMessage="Cheaper"
                    />
                  </Text>
                </Grid>
                <Grid mobileSm="fit">
                  <LegendBoxRed />
                </Grid>
                <Grid mobileSm="fit">
                  <Text variant="bodyXS" colorPath="content.neutral.secondary">
                    <FormattedMessageWrapper
                      id="higher"
                      defaultMessage="Higher"
                    />
                  </Text>
                </Grid>
              </Grid>
            </Legend>

            <Box paddingTop="s500">
              {!offersData && selectedDate && searchComplete && (
                <Text variant="titleL">
                  <Grid container spacing="s200" alignItems="center">
                    <Grid mobileSm="fit">
                      <Icon
                        size="md"
                        name="Forbid"
                        colorPath="content.danger.secondary"
                      />
                    </Grid>
                    <Grid mobileSm="fit">
                      <FormattedMessageWrapper
                        id="soldOut.messageWithDatesB"
                        defaultMessage="Sold out {checkIn} - {checkOut}"
                        values={{
                          checkIn: formatDate(
                            dateStringToMiddayDate(selectedDate),
                            {
                              day: '2-digit',
                              month: 'short'
                            }
                          ),
                          checkOut: formatDate(
                            getCheckOutDate(selectedDate, nights),
                            {
                              day: '2-digit',
                              month: 'short'
                            }
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Text>
              )}

              {offersData && searchComplete && (
                <OffersWrapper>
                  <OffersList
                    hotelId={hotelId}
                    offers={offersData}
                    sourceComponent={sourceComponent}
                  />
                </OffersWrapper>
              )}
            </Box>
          </Box>
        </>
      </OverlayPageContent>
    </OverlayPage>
  )
}
